import is from '@sindresorhus/is';
import type { FunctionComponent, ReactNode } from 'react';
import { Fragment, useContext } from 'react';
import type Maybe from 'true-myth/maybe';

import { ApplicationContext } from '~/shared/context/context';
import type { CustomAttributeWithValue } from '~/shared/data-contract/custom-attribute';
import type { User } from '~/shared/data-contract/user';

import { DataCell } from '../../../../../design/components/data-grid/DataGridContainer';
import { CustomAttributeComponent } from './CustomAttributeComponent';
import { SidebarCard } from './SidebarCard';

type DetailsProps = {
    readonly customAttributes: readonly CustomAttributeWithValue[];
    readonly detailActions?: ReactNode;
    readonly user: Maybe<User>;
};

type VisibleDetail = {
    readonly label: string;
    readonly value: string;
};

export const Details: FunctionComponent<DetailsProps> = ({ customAttributes, detailActions, user }) => {
    const { translate } = useContext(ApplicationContext);

    if (user.isNothing) {
        return null;
    }
    const { department, jobTitle } = user.value;

    const visibleDetails: readonly VisibleDetail[] = [
        ...(is.nonEmptyString(jobTitle)
            ? [
                  {
                      label: translate('pages.edit-profile-details.job-title'),
                      value: jobTitle,
                  },
              ]
            : []),
        ...(is.nonEmptyString(department)
            ? [
                  {
                      label: translate('pages.edit-profile-details.department'),
                      value: department,
                  },
              ]
            : []),
    ];

    return (
        <SidebarCard detailActions={detailActions} title={translate('pages.recruiter-profile.details.title')}>
            {/** biome-ignore lint/complexity/noUselessFragments: suppressed due to migration */}
            <Fragment>
                {visibleDetails.map(({ label, value }) => (
                    <DataCell key={label} label={label} value={value} />
                ))}
                {customAttributes.map((attribute) => (
                    <CustomAttributeComponent key={attribute.key} attribute={attribute} />
                ))}
            </Fragment>
        </SidebarCard>
    );
};
